import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ModernLanguageSwitcher from '../common/ModernLanguageSwitcher';
import NotificationBell from '../common/NotificationBell';
import NotificationDropdown from '../common/NotificationDropdown';
import api from '../../utils/api';
import {
  UserIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  CubeIcon,
  ShoppingBagIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

type SearchResult = {
  id: string | number;
  type: 'user' | 'product' | 'order';
  title: string;
  subtitle: string;
  icon: string;
  url: string;
};

const AdminHeader: React.FC<{ onMenuClick?: () => void }> = ({ onMenuClick }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { user, logout } = useAuth();
  const [isNotifOpen, setIsNotifOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ users: SearchResult[]; products: SearchResult[]; orders: SearchResult[] }>({ users: [], products: [], orders: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const roleKey = user?.role === 'admin'
    ? 'admin.admin'
    : user?.role === 'customer'
      ? 'admin.customer'
      : 'admin.undefined';
  const roleLabel = t(roleKey, user?.role || '');

  // Debounced search
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults({ users: [], products: [], orders: [] });
      setShowSearchResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await api.get(`/admin/dashboard/search?q=${encodeURIComponent(searchQuery)}&limit=5`);
        setSearchResults(response.data);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults({ users: [], products: [], orders: [] });
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = useCallback((result: SearchResult) => {
    setSearchQuery('');
    setShowSearchResults(false);
    navigate(result.url);
  }, [navigate]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'user':
        return <UserIcon className="h-4 w-4" />;
      case 'product':
        return <CubeIcon className="h-4 w-4" />;
      case 'order':
        return <ShoppingBagIcon className="h-4 w-4" />;
      default:
        return <MagnifyingGlassIcon className="h-4 w-4" />;
    }
  };

  const totalResults = searchResults.users.length + searchResults.products.length + searchResults.orders.length;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
      <div className="px-2 sm:px-3 lg:px-6">
        <div className="flex items-center h-12 sm:h-14 justify-between">
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              className="p-1.5 sm:p-2 text-gray-700 hover:text-emerald-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              onClick={onMenuClick}
              aria-label={t('admin.openMenu', 'Open menu')}
            >
              <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Search Bar - Desktop Only */}
          <div className="hidden md:flex flex-1 max-w-md mx-4" ref={searchRef}>
            <div className="relative w-full">
              <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-2.5' : 'left-0 pl-2.5'} flex items-center pointer-events-none z-10`}>
                {isSearching ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                ) : (
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                )}
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.trim().length >= 2 && setShowSearchResults(true)}
                className={`block w-full ${isRTL ? 'pr-8 pl-2.5' : 'pl-8 pr-2.5'} py-1.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-sm`}
                placeholder={t('admin.searchPlaceholder', 'Search orders, users, products...')}
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }}
                  className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-2.5' : 'right-0 pr-2.5'} flex items-center z-10`}
                >
                  <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}

              {/* Search Results Dropdown */}
              {showSearchResults && totalResults > 0 && (
                <div className={`absolute ${isRTL ? 'right-0' : 'left-0'} mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto`}>
                  {/* Users Section */}
                  {searchResults.users.length > 0 && (
                    <div className="p-2">
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase">
                        {t('admin.users', 'Users')}
                      </div>
                      {searchResults.users.map((result) => (
                        <button
                          key={`user-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                        >
                          <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                            {getIcon('user')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{result.title}</div>
                            <div className="text-xs text-gray-500 truncate">{result.subtitle}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Products Section */}
                  {searchResults.products.length > 0 && (
                    <div className="p-2 border-t border-gray-100">
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase">
                        {t('admin.products', 'Products')}
                      </div>
                      {searchResults.products.map((result) => (
                        <button
                          key={`product-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                        >
                          <div className="flex-shrink-0 h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">
                            {getIcon('product')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{result.title}</div>
                            <div className="text-xs text-gray-500 truncate">{result.subtitle}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Orders Section */}
                  {searchResults.orders.length > 0 && (
                    <div className="p-2 border-t border-gray-100">
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase">
                        {t('admin.orders', 'Orders')}
                      </div>
                      {searchResults.orders.map((result) => (
                        <button
                          key={`order-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                        >
                          <div className="flex-shrink-0 h-8 w-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
                            {getIcon('order')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{result.title}</div>
                            <div className="text-xs text-gray-500 truncate">{result.subtitle}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* No Results */}
              {showSearchResults && searchQuery.trim().length >= 2 && !isSearching && totalResults === 0 && (
                <div className={`absolute ${isRTL ? 'right-0' : 'left-0'} mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-200 z-50 p-4 text-center`}>
                  <p className="text-sm text-gray-500">{t('admin.noSearchResults', 'No results found')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Right side actions */}
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-1 sm:space-x-2' : 'space-x-1 sm:space-x-2'} px-1`}>

            {/* Notification Bell: dropdown on md+, navigate on mobile */}
            <div className="relative hidden sm:block">
              <NotificationBell onClick={() => setIsNotifOpen(v => !v)} />
              <NotificationDropdown isOpen={isNotifOpen} onClose={() => setIsNotifOpen(false)} />
            </div>
            <div className="sm:hidden">
              <NotificationBell onClick={() => navigate('/admin/notifications')} />
            </div>

            {/* Language Switcher */}
            <div className="hidden sm:block">
              <ModernLanguageSwitcher />
            </div>

            {/* User Info & Logout */}
            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-1 sm:space-x-2' : 'space-x-1 sm:space-x-2'}`}>
              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-1 sm:space-x-1.5' : 'space-x-1 sm:space-x-1.5'}`}>
                <div className="h-6 w-6 sm:h-7 sm:w-7 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                  <UserIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" />
                </div>
                <div className={`hidden sm:block ${isRTL ? 'text-right' : 'text-left'}`}>
                  <p className="text-xs font-medium text-gray-700">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">{roleLabel}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="p-1 sm:p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                title={t('admin.logout', 'Logout')}
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
